/**
 * @providesModule LocationPermissionController
 *
 */

import React from 'react';
import GlobalEventEmitter from 'GlobalEventEmitter';
import {_m} from 'INTL';
import ModalNavigationController from 'ModalNavigationController';
import OrientationTransitionController from 'OrientationTransitionController';
import Prefs from 'Preferences';
import SKZConstants from 'SKZConstants';
import StaticWarningModal from 'StaticWarningModal';

import {NativeModules, Platform} from 'react-native';

const ReactVersusManager = NativeModules.SKZReactVersusManager;
const SkillzModule = NativeModules.SkillzModule;

//these values extracted from CLAuthorizationStatus constants
const LOCATION_PERMISSION_DENIED = 2;
const LOCATION_PERMISSION_RESTRICTED = 1;
const LOCATION_PERMISSION_UNDETERMINED = 0;
const LOCATION_PERMISSION_AUTHORIZED = 4; // we're getting 4 for 'allowed once' and 'allow while using app' on iOS

class LocationPermissionController extends React.Component {
  constructor() {
    super();
    this.locationEnabled = Prefs.get(SKZConstants.LOCATION_AUTHORIZATION_ENABLED);
    this.updateLocationStatus = this._updateLocationStatus.bind(this);
    this._addListeners();
  }

  _addListeners() {
    this._removeListeners();
    this.subscriptions = [];
    this.subscriptions.push(
      GlobalEventEmitter.addListener('locationAuthStatusChanged', this.updateLocationStatus)
    );
  }

  _removeListeners() {
    if (this.subscriptions) {
      this.subscriptions.forEach((listener: unknown): void => {
        if (listener) {
          listener.remove();
        }
      });
      this.subscriptions = [];
    }
  }

  _updateLocationStatus(status: number) {
    const enabled =
      status != LOCATION_PERMISSION_DENIED &&
      status != LOCATION_PERMISSION_RESTRICTED &&
      status != LOCATION_PERMISSION_UNDETERMINED;
    this.locationEnabled = enabled;
    Prefs.set({[SKZConstants.LOCATION_AUTHORIZATION_ENABLED]: enabled});
  }

  // NOT USED AT THE MOMENT
  requestPermission(completion: () => {} = null) {
    SkillzModule.promptLocation((result: boolean) => {
      this.locationEnabled = result;
      Prefs.set({[SKZConstants.LOCATION_AUTHORIZATION_ENABLED]: result});
      if (completion) {
        completion(result);
      }
    });
  }

  requestPermissionAndroid(completion: () => {} = null) {
    this._startLocationManager()
      .then(() => {
        completion(this.locationEnabled);
      })
      .catch((error: string) => {
        console.debug("MatchJoinController: Error getting user's location: " + error);
      });
  }

  async promptForLocationWithOTASoftPrompt(
    hasShownHardPrompt: boolean = false,
    callback: () => {} = null,
    isDepositScreen: boolean = false
  ): Promise<boolean> {
    const title = _m('Need Your Location');
    const message = _m('Allow location access to check tournament eligibility.');
    if (Prefs.get(SKZConstants.LOCATION_SOFT_PROMPT_SEEN) || Platform.OS === 'ios') {
      return this.promptForLocation(hasShownHardPrompt, callback, isDepositScreen);
    }
    return new Promise((resolve: (boolean) => void) => {
      this._showPermissionModal(title, message, false, async (buttonIndex: number) => {
        ModalNavigationController.hideModal();
        if (!buttonIndex) {
          this._setLocationEnabled(false);
          resolve(false);
        } else {
          Prefs.set({[SKZConstants.LOCATION_SOFT_PROMPT_SEEN]: true});
          resolve(await this.promptForLocation(hasShownHardPrompt, callback, isDepositScreen));
        }
      });
    });
  }

  async promptForLocation(
    hasShownHardPrompt: boolean = false,
    callback: (index: number) => void = null,
    isDepositScreen: boolean = false
  ): Promise<boolean> {
    const title = _m('Need Your Location');
    let message = _m('To join this tournament, enable location services in your settings.');
    if (hasShownHardPrompt && Platform.OS !== 'ios') {
      this._showPermissionModal(title, message, hasShownHardPrompt, callback); // this modal makes apple reject our apps from the app store
    } else {
      // eslint-disable-next-line no-useless-catch
      try {
        message = isDepositScreen
          ? _m(
              'Allow location access to check eligibility for skill-based cash gaming in your region.'
            )
          : _m('Allow location access to check tournament eligibility.');
        const didGetLocation = await this._displayLocationPrompt(title, message);
        this._setLocationEnabled(didGetLocation);
        if (!didGetLocation) {
          await OrientationTransitionController.updateOrientationToPortrait();
          if (callback) {
            if (Platform.OS == 'ios') {
              const authorizationStatus = await ReactVersusManager.getAuthorizationStatus();
              if (authorizationStatus === LOCATION_PERMISSION_DENIED) {
                callback();
              }
            } else {
              // Updated to have Android show callback as well
              callback();
            }
          }
          // This error is internal only and does not need to be translated
          throw new Error('Failed to get location');
        } else {
          return true;
        }
      } catch (error) {
        throw error;
      }
    }
  }

  /**
   * Prompts the user to enable location services for the app.
   * @param hasShownHardPrompt if the hard prompt has been shown, then shows the soft prompt (only for Android)
   * @param isDepositScreen shows a different message if the user is on the deposit screen
   * @returns `Promise<boolean>` if hard prompt has been shown, or `Promise<number>` with button index if the soft prompt has been shown
   */
  async promptForLocationAsync(
    hasShownHardPrompt: boolean = false,
    isDepositScreen: boolean = false,
    enableHardPrompt: boolean = false
  ): Promise<boolean | number> {
    const title = _m('Need Your Location');
    const softPromptMessage = _m(
      'To join this tournament, enable location services in your settings.'
    );
    const hardPromptMessage = isDepositScreen
      ? _m('Allow location access to check eligibility for skill-based cash gaming in your region.')
      : _m('Allow location access to check tournament eligibility.');

    if (hasShownHardPrompt && Platform.OS !== 'ios') {
      return new Promise((resolve) => {
        this._showPermissionModal(title, softPromptMessage, false, (buttonIndex?: number) =>
          resolve.bind(buttonIndex ?? false)
        );
      });
    }

    try {
      const didGetLocation = await this._displayLocationPrompt(
        title,
        hardPromptMessage,
        enableHardPrompt
      );

      this._setLocationEnabled(didGetLocation);

      if (didGetLocation) {
        return true;
      } else {
        // handle the case where the user denies location permission or it fails
        await OrientationTransitionController.updateOrientationToPortrait();
        if (Platform.OS === 'ios') {
          const authorizationStatus = await ReactVersusManager.getAuthorizationStatus();

          // `_displayLocationPrompt` returns false right after user allows location permission prompt on iOS
          // but we can get the updated status from the native module
          if (authorizationStatus === LOCATION_PERMISSION_AUTHORIZED) {
            return true;
          }

          if (authorizationStatus === LOCATION_PERMISSION_DENIED) {
            return false;
          }
        } else {
          return false;
        }

        // This error is internal only and does not need to be translated
        throw new Error('Failed to get location');
      }
    } catch (error) {
      throw error;
    }
  }

  async _startLocationManager(): Promise<boolean> {
    const title = _m('Need Your Location');
    const message = _m('Allow location access to check tournament eligibility.');
    try {
      const didGetLocation = await this._displayLocationPrompt(title, message);
      this._setLocationEnabled(didGetLocation);
    } catch (error) {
      console.debug(error);
      this.locationEnabled = false;
      return false;
    }
  }

  async _displayLocationPrompt(
    title: string,
    message: string,
    enableHardPrompt: boolean = false
  ): Promise<boolean> {
    if (Platform.OS === 'web' && this.locationEnabled) {
      return true;
    }

    // Check current authorization status before trying to prompt
    if (Platform.OS === 'ios' && ReactVersusManager.getAuthorizationStatus) {
      try {
        const currentStatus = await ReactVersusManager.getAuthorizationStatus();
        // if enableHardPrompt is true, we will show native hard prompt
        if (!enableHardPrompt && currentStatus === 2) {
          // LOCATION_PERMISSION_DENIED
          return false;
        }
        if (currentStatus === 4) {
          // LOCATION_PERMISSION_AUTHORIZED
          return true;
        }
      } catch (error) {
        // Continue to prompt if status check fails
      }
    }

    return ReactVersusManager.startLocationManager({
      title: title,
      message: message,
    });
  }

  _setLocationEnabled(locationEnabled: boolean) {
    this.locationEnabled = locationEnabled;
    Prefs.set({[SKZConstants.LOCATION_AUTHORIZATION_ENABLED]: locationEnabled});
  }

  _showPermissionModal(
    title: string,
    message: string,
    hasShownHardPrompt: boolean,
    callback: () => {}
  ) {
    // WARNING : this modal makes apple reject our apps from the app store, need to consider fully deprecating it
    const modalProps = {
      animationType: 'fade',
      transparent: true,
    };
    const modal = (
      <StaticWarningModal
        title={title}
        titleTestID={'location_modal_title'}
        message={message}
        messageTestID={'location_modal_message'}
        hasTitleMessageDivider={true}
        buttonTitleArray={[_m('NOT NOW'), hasShownHardPrompt ? _m('SETTINGS') : _m('ALLOW')]}
        buttonTestIDArray={['notnow', 'allow']}
        callback={callback}
      />
    );
    ModalNavigationController.showModal(modal, modalProps);
  }

  get isLocationEnabled(): boolean {
    return this.locationEnabled;
  }

  get hasShownHardPrompt(): boolean {
    return this.locationEnabled != null;
  }
}

export default new LocationPermissionController();
